import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Send,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Mail,
  ArrowUpRight,
} from "lucide-react";

import { person } from "../data/profile";
import { cn } from "../lib/utils";

/**
 * InlineContactForm
 *
 * Every landing page ends with this rather than a link to /contact.
 *
 * The reason is conversion, not SEO: someone who lands on a hiring page
 * from search has already stated their intent by the query they typed.
 * Sending them to another page to restate it loses most of them.
 *
 * `source` is passed through to the API so the admin inbox shows which
 * page each enquiry came from — which is the only reliable way to know
 * whether a given landing page is doing anything.
 */
export default function InlineContactForm({
  source = "Landing Page",
  heading,
  body,
  compact = false,
}) {
  const [form, setForm] = useState({ name: "", email: "", message: "", hp: "" });
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);

  const onChange = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus({ type: "error", text: "Name, email and a short message are all needed." });
      return;
    }
    if (form.hp) return; // honeypot

    setBusy(true);
    setStatus(null);
    try {
      const res = await fetch("/api/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          message: form.message,
          source,
        }),
      });
      if (res.ok) {
        setStatus({ type: "success", text: "Sent. You'll hear back within a working day." });
        setForm({ name: "", email: "", message: "", hp: "" });
      } else {
        setStatus({
          type: "error",
          text: "The server didn't accept that — email works as a fallback.",
        });
      }
    } catch {
      setStatus({ type: "error", text: "Network error. Email works as a fallback." });
    } finally {
      setBusy(false);
      window.setTimeout(() => setStatus(null), 7000);
    }
  };

  const field =
    "w-full rounded-2xl border border-white/[0.08] bg-white/[0.025] px-4 py-3.5 " +
    "text-[0.9rem] text-slate-100 placeholder-slate-600 outline-none " +
    "transition-colors duration-300 focus:border-brand-400/50 focus:bg-white/[0.04]";

  return (
    <section
      id="enquire"
      aria-labelledby="enquire-heading"
      className="panel scroll-mt-28 overflow-hidden rounded-4xl"
    >
      <div className={cn("grid gap-8 p-6 sm:p-9", !compact && "lg:grid-cols-2 lg:gap-12")}>
        <div>
          <h2
            id="enquire-heading"
            className="text-fluid-xl font-semibold leading-tight text-slate-50"
          >
            {heading || "Start a conversation"}
          </h2>
          <p className="mt-4 max-w-prose2 text-fluid-sm leading-relaxed text-slate-400">
            {body ||
              "Describe what you're building and where it is now. You'll get a straight answer about approach and fit."}
          </p>

          <div className="mt-7 space-y-3 border-t border-white/[0.06] pt-6">
            <a
              href={`mailto:${person.email}`}
              className="group flex items-center gap-3 text-sm text-slate-300 transition-colors hover:text-brand-300"
            >
              <Mail className="h-4 w-4 text-brand-400/70" />
              <span className="break-all">{person.email}</span>
              <ArrowUpRight className="h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
            </a>
            <p className="text-[0.78rem] text-slate-500">
              {person.location.city}, {person.location.region}, India · Delhi NCR ·
              Remote worldwide
            </p>
            <p className="text-[0.78rem] text-slate-500">
              Usually replies within one working day.
            </p>
          </div>
        </div>

        <form onSubmit={onSubmit} noValidate className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="mb-2 block font-mono text-[0.6rem] uppercase tracking-label text-slate-500">
                Your name
              </span>
              <input
                name="name"
                value={form.name}
                onChange={onChange}
                autoComplete="name"
                placeholder="Jane Sharma"
                className={field}
              />
            </label>
            <label className="block">
              <span className="mb-2 block font-mono text-[0.6rem] uppercase tracking-label text-slate-500">
                Email
              </span>
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                autoComplete="email"
                placeholder="jane@company.com"
                className={field}
              />
            </label>
          </div>

          <label className="block">
            <span className="mb-2 block font-mono text-[0.6rem] uppercase tracking-label text-slate-500">
              What are you building?
            </span>
            <textarea
              name="message"
              rows={5}
              value={form.message}
              onChange={onChange}
              placeholder="A sentence or two about the product, the team, and what's currently in the way."
              className={cn(field, "min-h-[7rem] resize-y")}
            />
          </label>

          {/* Honeypot — hidden from humans, irresistible to bots */}
          <div className="sr-only-seo" aria-hidden="true">
            <label>
              Leave this empty
              <input
                name="hp"
                value={form.hp}
                onChange={onChange}
                tabIndex={-1}
                autoComplete="off"
              />
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <button type="submit" disabled={busy} className="btn-primary disabled:opacity-60">
              {busy ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  Send message
                </>
              )}
            </button>
            <p className="text-[0.72rem] text-slate-600">
              Goes straight to my inbox. No list, no newsletter.
            </p>
          </div>

          <AnimatePresence>
            {status && (
              <motion.p
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                role="status"
                className={cn(
                  "flex items-start gap-2.5 rounded-2xl border px-4 py-3 text-[0.82rem]",
                  status.type === "success"
                    ? "border-brand-400/30 bg-brand-500/[0.08] text-brand-200"
                    : "border-rose-400/30 bg-rose-500/[0.08] text-rose-200"
                )}
              >
                {status.type === "success" ? (
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                ) : (
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                )}
                {status.text}
              </motion.p>
            )}
          </AnimatePresence>
        </form>
      </div>
    </section>
  );
}
